import { useState, useCallback } from "react";
import { getSymbolName, codepointLabel } from "@/data/symbol-names";
import { copySymbolAriaLabel } from "@/lib/symbol-aria";
import { copyText } from "@/lib/copy";
import { CollectButton } from "@/components/CollectButton";


interface SymbolGridProps {
  symbols: string[];
  size?: "sm" | "md" | "lg";
  /** Optional Tailwind text-color class to tint the glyphs (e.g. per category). */
  accentClass?: string;
}

export function SymbolGrid({ symbols, size = "md", accentClass = "" }: SymbolGridProps) {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const handleCopy = useCallback(async (sym: string, idx: number) => {
    const ok = await copyText(sym, { source: "symbol-tile", symbol: sym });
    if (!ok) return;
    setCopiedIdx(idx);
    window.setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 900);
  }, []);

  const cols =
    size === "lg"
      ? "grid-cols-4 sm:grid-cols-6 md:grid-cols-8"
      : size === "sm"
        ? "grid-cols-6 sm:grid-cols-8 md:grid-cols-12"
        : "grid-cols-5 sm:grid-cols-7 md:grid-cols-10";

  const fontSize = size === "lg" ? "text-2xl sm:text-4xl" : size === "sm" ? "text-lg sm:text-xl" : "text-xl sm:text-2xl";

  return (
    <div className={`grid gap-2 sm:gap-3 ${cols}`}>
      {symbols.map((s, i) => {
        const name = getSymbolName(s);
        return (
          <div key={`${s}-${i}`} className="group relative">
            <button
              type="button"
              // aria-label is announced by screen readers; `title` gives sighted
              // users a native hover tooltip with the symbol's name.
              aria-label={copySymbolAriaLabel({
                name,
                code: codepointLabel(s),
                symbol: s,
              })}
              title={`${name} — click to copy`}
              className={`symbol-tile symbol-tile-copy w-full ${fontSize} ${accentClass} ${copiedIdx === i ? "copied" : ""}`}
              onClick={() => handleCopy(s, i)}
            >
              <span aria-hidden="true">{s}</span>
            </button>
            <CollectButton symbol={s} />
          </div>
        );
      })}
    </div>
  );
}
